import React, { useState } from "react";

/**
 * A collapsible section for displaying feedback items.
 *
 * @param {object} props - The component's props.
 * @param {string} props.title - The title of the feedback section.
 * @param {React.ReactNode} props.children - The content to be displayed within the section.
 * @param {boolean} [props.initiallyOpen=false] - Whether the section should be open by default.
 * @param {'green'|'yellow'|'orange'|'red'|'purple'|'blue'|'slate'} [props.color='blue'] - The color theme for the section.
 * @param {string} [props.className] - Additional CSS classes for the outer div.
 */
const FeedbackSection = ({
  title,
  children,
  initiallyOpen = false,
  color = "blue",
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(initiallyOpen);

  // Tailwind CSS color classes for background gradients and border/text colors
  // Added a 'slate' option for more neutral feedback if needed.
  const colorThemes = {
    green: {
      gradient: "from-green-50 to-emerald-100",
      border: "border-green-200",
      text: "text-green-800",
      icon: "text-green-500", // For potential icons within the header
    },
    yellow: {
      gradient: "from-yellow-50 to-amber-100",
      border: "border-yellow-200",
      text: "text-yellow-800",
      icon: "text-yellow-500",
    },
    orange: {
      gradient: "from-orange-50 to-red-100", // Or a more distinct orange range
      border: "border-orange-200",
      text: "text-orange-800",
      icon: "text-orange-500",
    },
    red: {
      gradient: "from-red-50 to-pink-100",
      border: "border-red-200",
      text: "text-red-800",
      icon: "text-red-500",
    },
    purple: {
      gradient: "from-purple-50 to-indigo-100",
      border: "border-purple-200",
      text: "text-purple-800",
      icon: "text-purple-500",
    },
    blue: {
      gradient: "from-blue-50 to-indigo-100",
      border: "border-blue-200",
      text: "text-blue-800",
      icon: "text-blue-500",
    },
    slate: {
      gradient: "from-slate-100 to-slate-200",
      border: "border-slate-300",
      text: "text-slate-700",
      icon: "text-slate-500",
    },
  };

  const selectedTheme = colorThemes[color] || colorThemes.blue; // Fallback to blue

  return (
    <div
      className={`bg-gradient-to-r ${selectedTheme.gradient} ${selectedTheme.border} ${selectedTheme.text} rounded-xl shadow-lg border p-4 sm:p-5 ${className}`}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between text-left focus:outline-none"
        aria-expanded={isOpen}
        aria-controls={`feedback-content-${title
          .replace(/\s+/g, "-")
          .toLowerCase()}`} // Accessibility
      >
        <h4 className="text-md sm:text-lg font-semibold">{title}</h4>
        <span
          className={`transform transition-transform duration-200 ease-in-out ${
            isOpen ? "rotate-180" : "rotate-0"
          }`}
          aria-hidden="true" // Decorative icon
        >
          {/* Chevron down icon */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 sm:h-6 sm:w-6"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </span>
      </button>

      {/* Using a more robust way to handle conditional rendering for animation/transition */}
      <div
        id={`feedback-content-${title.replace(/\s+/g, "-").toLowerCase()}`}
        className={`transition-all duration-300 ease-in-out ${
          isOpen
            ? "opacity-100 mt-4 pt-4 border-t border-current border-opacity-20"
            : "hidden opacity-0"
        }`}
      >
        {isOpen && ( // Render children only when open to avoid issues if children depend on being visible
          <div className="max-h-[500px] overflow-y-auto pr-1">
            {children}
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedbackSection;
